// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — Firebase Service

import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  onAuthStateChanged,
  User,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
  QueryConstraint,
  enableMultiTabIndexedDbPersistence,
} from 'firebase/firestore';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

// ─────────────────────────────────────────────────────────────────────────────
// FIREBASE CONFIG
// ─────────────────────────────────────────────────────────────────────────────

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID as string,
};

// ─────────────────────────────────────────────────────────────────────────────
// SINGLETON INIT
// ─────────────────────────────────────────────────────────────────────────────

let app: FirebaseApp;
if (!getApps().length) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

export const auth = getAuth(app);
export const db   = getFirestore(app);
export const fns  = getFunctions(app, 'us-central1');

// Offline persistence
enableMultiTabIndexedDbPersistence(db).catch(err => {
  if (err.code === 'failed-precondition') {
    console.warn('Firestore persistence disabled (multiple tabs open)');
  } else if (err.code === 'unimplemented') {
    console.warn('Firestore persistence not supported in this browser');
  }
});

// App Check — reCAPTCHA Enterprise (production only)
const recaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string;
if (import.meta.env.PROD && recaptchaKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaKey),
    isTokenAutoRefreshEnabled: true,
  });
}

// Emulator (local dev)
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATOR === 'true') {
  connectFunctionsEmulator(fns, 'localhost', 5001);
}

// ─────────────────────────────────────────────────────────────────────────────
// AUTH HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export async function signIn(email: string, password: string) {
  return signInWithEmailAndPassword(auth, email, password);
}

export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  return signInWithPopup(auth, provider);
}

export async function signOut() {
  return fbSignOut(auth);
}

export function onAuth(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback);
}

// ─────────────────────────────────────────────────────────────────────────────
// APP ERROR TYPE
// ─────────────────────────────────────────────────────────────────────────────

export interface AppError {
  code: string;
  message: string;
  retryable: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// CALL FUNCTION — retry with exponential backoff
// ─────────────────────────────────────────────────────────────────────────────

const retryableCodes = new Set(['unavailable', 'resource-exhausted', 'deadline-exceeded']);

export async function callFunction<T = unknown>(
  name: string,
  data?: unknown,
  maxAttempts = 3
): Promise<T> {
  let lastErr: AppError = { code: 'unknown', message: 'Unknown error', retryable: false };

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const fn = httpsCallable<unknown, T>(fns, name);
      const result = await fn(data);
      return result.data;
    } catch (err: unknown) {
      const raw = err as { code?: string; message?: string };
      lastErr = {
        code:      raw.code ?? 'unknown',
        message:   humanizeError(raw.code ?? '', raw.message ?? ''),
        retryable: retryableCodes.has(raw.code ?? ''),
      };
      if (!lastErr.retryable || attempt === maxAttempts) throw lastErr;
      await sleep(Math.min(1000 * 2 ** (attempt - 1), 8000));
    }
  }
  throw lastErr;
}

function humanizeError(code: string, message: string): string {
  // Firebase SDK returns callable error codes as 'functions/permission-denied', etc.
  const stripped = code.replace(/^functions\//, '');
  const map: Record<string, string> = {
    'unauthenticated':     'Your session has expired. Please sign in again.',
    'permission-denied':   "You don't have permission to do that.",
    'resource-exhausted':  'Too many requests. Please wait and try again.',
    'unavailable':         'Service temporarily unavailable. Please try again.',
    'not-found':           'Item not found.',
    'already-exists':      'Item already exists.',
    'invalid-argument':    'Invalid input. Please check your data.',
    'failed-precondition': message || 'Action cannot be completed right now.',
  };
  return map[stripped] ?? 'Something went wrong. Please try again.';
}

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

// ─────────────────────────────────────────────────────────────────────────────
// FIRESTORE HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export async function getDocument<T>(path: string): Promise<T | null> {
  const snap = await getDoc(doc(db, path));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as T) : null;
}

export function subscribeToDoc<T>(
  path: string,
  onData: (data: T | null) => void,
  onErr?: (err: Error) => void
): () => void {
  return onSnapshot(
    doc(db, path),
    snap => onData(snap.exists() ? ({ id: snap.id, ...snap.data() } as T) : null),
    err => onErr?.(err)
  );
}

export function subscribeToQuery<T>(
  collectionPath: string,
  constraints: QueryConstraint[],
  onData: (items: T[]) => void,
  onErr?: (err: Error) => void
): () => void {
  const q = query(collection(db, collectionPath), ...constraints);
  return onSnapshot(
    q,
    snap => onData(snap.docs.map(d => ({ id: d.id, ...d.data() }) as T)),
    err => onErr?.(err)
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RE-EXPORT Firestore utilities
// ─────────────────────────────────────────────────────────────────────────────

export {
  doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  collection, query, where, orderBy, limit, onSnapshot,
  Timestamp, serverTimestamp, increment, arrayUnion, arrayRemove,
};

// ─────────────────────────────────────────────────────────────────────────────
// RUNTIME BRANDING
// ─────────────────────────────────────────────────────────────────────────────

export interface BrandingConfig {
  appName?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  logoUrl?: string;
  fontFamily?: string | null;
}

export function applyBranding(config: BrandingConfig): void {
  const root = document.documentElement;
  if (config.primaryColor)   root.style.setProperty('--color-primary', config.primaryColor);
  if (config.secondaryColor) root.style.setProperty('--color-secondary', config.secondaryColor);
  if (config.accentColor)    root.style.setProperty('--color-accent', config.accentColor);
  if (config.appName)        document.title = `${config.appName} Admin`;
  if (config.logoUrl) {
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) favicon.href = config.logoUrl;
  }
  if (config.fontFamily) {
    root.style.setProperty('--font-body', `'${config.fontFamily}', system-ui, sans-serif`);
  }
}

export function resetBranding(): void {
  const root = document.documentElement;
  ['--color-primary','--color-secondary','--color-accent','--font-body']
    .forEach(p => root.style.removeProperty(p));
  document.title = 'Aphilaty Admin';
}
